/**
 * PHRASE BLOOM のフレーズ生成ロジック。
 *
 * すべて純関数 + シード付き PRNG で構成し、同一パラメータ・同一シードなら
 * 常に同じフレーズを返す（UI のノブを回すたびに結果が確定的に変わる）。
 * 出力は PPQ 480 の tick ベースで、MIDI 書き出し・Web Audio 再生・
 * ピアノロール描画がそのまま共有する。
 */

import {
  buildScalePool,
  getCadencePitchClasses,
  getScaleById,
  rootMidiOf,
  type CadenceId,
  type ScaleId,
} from "./musicTheory";

export const PPQ = 480;
export const TICKS_PER_BAR = PPQ * 4; // 4/4 固定

/** END POINT の選択肢（bar.beat.tick 表記、先頭ノートは常に 1.1.0） */
export interface EndPointDef {
  id: string;
  label: string;
  ticks: number;
}

const endPoint = (bar: number, beat: number): EndPointDef => ({
  id: `${bar}.${beat}.0`,
  label: `${bar}.${beat}.0`,
  ticks: (bar - 1) * TICKS_PER_BAR + (beat - 1) * PPQ,
});

export const END_POINTS: EndPointDef[] = [
  endPoint(1, 2),
  endPoint(1, 3),
  endPoint(1, 4),
  endPoint(2, 1),
  endPoint(2, 2),
  endPoint(2, 3),
  endPoint(2, 4),
  endPoint(3, 1),
];

export interface PhraseParams {
  bpm: number;
  /** ノート数 2..32 */
  noteCount: number;
  /** END_POINTS の id */
  endPointId: string;
  /** 上昇:キープ:下降 の重み（各 0..100） */
  riseWeight: number;
  keepWeight: number;
  fallWeight: number;
  /** 加速度 -50..50（+で後半ほど間隔が短く・末尾ほど強く、−は頭が強くなる） */
  accel: number;
  /** ゲート 10..200 (%) */
  gate: number;
  /** グライド（跳躍時に前の音を次の音までつなぎ、ピッチをなめらかに遷移させる） */
  glide: boolean;
  cadence: CadenceId;
  /** ルートのピッチクラス 0..11（C=0） */
  rootPc: number;
  /** 開始地点のオクターブ（C4=60 基準の表記、C0 から指定可） */
  baseOctave: number;
  scaleId: ScaleId;
  seed: number;
}

export interface PhraseNote {
  tick: number;
  midiNote: number;
  durationTicks: number;
  /** 1..127 */
  velocity: number;
  /** グライド先の MIDI ノート（跳躍をつなぐとき、音の末尾でこのピッチへ遷移） */
  glideToMidi?: number;
}

export interface GeneratedPhrase {
  notes: PhraseNote[];
  /** 最終ノートの開始 tick（= END POINT） */
  endTick: number;
  /** フレーズ全体の長さ（終点を含む小節境界まで切り上げた tick） */
  lengthTicks: number;
}

export const DEFAULT_PARAMS: PhraseParams = {
  bpm: 140,
  noteCount: 12,
  endPointId: "2.1.0",
  riseWeight: 80,
  keepWeight: 10,
  fallWeight: 10,
  accel: 30,
  gate: 100,
  glide: true,
  cadence: "tonic",
  rootPc: 0,
  baseOctave: 4,
  scaleId: "majorPentatonic",
  seed: 42,
};

/** mulberry32: 軽量で分布の良い 32bit シード PRNG */
export const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/**
 * ノート開始位置の配列を作る。
 * 正規化位置 x^p（p は加速度から算出）で 0..endTick に配置し、
 * 丸め後に単調増加を保証、末尾は必ず endTick に固定する。
 */
const buildOnsetTicks = (
  noteCount: number,
  endTick: number,
  accel: number,
): number[] => {
  if (noteCount === 1) return [endTick];
  // accel は -50..50。+50 → p≈0.63（後半密 = 加速）、-50 → p≈1.58（後半疎 = 減速）
  const p = Math.pow(2.5, -accel / 100);
  const ticks: number[] = [];
  for (let i = 0; i < noteCount; i++) {
    const x = i / (noteCount - 1);
    ticks.push(Math.round(endTick * Math.pow(x, p)));
  }
  for (let i = 1; i < ticks.length; i++) {
    if (ticks[i] <= ticks[i - 1]) ticks[i] = ticks[i - 1] + 1;
  }
  // 単調化で末尾がずれた場合は endTick へ戻し、後ろから詰める
  ticks[ticks.length - 1] = endTick;
  for (let i = ticks.length - 2; i >= 0; i--) {
    if (ticks[i] >= ticks[i + 1]) ticks[i] = ticks[i + 1] - 1;
  }
  return ticks;
};

/**
 * スケールプール上のインデックス列（メロディ輪郭）を作る。
 *
 * 開始地点（KEY + OCTAVE のルート音）から、上昇/キープ/下降の重みに従い
 * 順次進行（1〜2 度）+ 時々跳躍（3〜5 度）で自在に歩く。音域の制限は
 * 設けず、プール端（= MIDI 0..127）でのみクランプする。
 */
const buildContour = (
  noteCount: number,
  poolSize: number,
  startIdx: number,
  riseWeight: number,
  keepWeight: number,
  fallWeight: number,
  rand: () => number,
): number[] => {
  const total = riseWeight + keepWeight + fallWeight;
  const rise = total > 0 ? riseWeight / total : 1 / 3;
  const keep = total > 0 ? keepWeight / total : 1 / 3;

  let idx = clamp(startIdx, 0, poolSize - 1);
  const indices: number[] = [idx];
  for (let i = 1; i < noteCount; i++) {
    const r = rand();
    let direction: -1 | 0 | 1;
    if (r < rise) direction = 1;
    else if (r < rise + keep) direction = 0;
    else direction = -1;

    if (direction === 0) {
      indices.push(idx);
      continue;
    }

    // 順次進行（1〜2度）を基本に、たまに跳躍（3〜5度）
    const leap = rand() < 0.18;
    const step = leap ? 3 + Math.floor(rand() * 3) : 1 + Math.floor(rand() * 2);

    idx = clamp(idx + direction * step, 0, poolSize - 1);
    indices.push(idx);
  }
  return indices;
};

/** 最終ノートを終止形の構成音へスナップする */
const snapToCadence = (
  pool: number[],
  lastIdx: number,
  prevIdx: number,
  cadencePcs: number[],
): number => {
  const lastMidi = pool[lastIdx];
  let best = lastIdx;
  let bestDist = Infinity;
  const risingEnd = lastIdx >= prevIdx;
  for (let i = 0; i < pool.length; i++) {
    if (!cadencePcs.includes(((pool[i] % 12) + 12) % 12)) continue;
    const dist = Math.abs(pool[i] - lastMidi);
    // 同距離ならフレーズの進行方向側を優先
    const better =
      dist < bestDist ||
      (dist === bestDist && (risingEnd ? pool[i] > pool[best] : pool[i] < pool[best]));
    if (better) {
      best = i;
      bestDist = dist;
    }
  }
  return best;
};

/**
 * フレーズ生成のエントリポイント。
 */
export const generatePhrase = (params: PhraseParams): GeneratedPhrase => {
  const {
    noteCount,
    endPointId,
    riseWeight,
    keepWeight,
    fallWeight,
    accel,
    gate,
    glide,
    cadence,
    rootPc,
    baseOctave,
    scaleId,
    seed,
  } = params;

  const endDef = END_POINTS.find((e) => e.id === endPointId) ?? END_POINTS[3];
  const endTick = endDef.ticks;
  const rand = mulberry32(seed);
  const scale = getScaleById(scaleId);
  const pool = buildScalePool(rootPc, scale);
  const startIdx = pool.indexOf(rootMidiOf(rootPc, baseOctave));

  const onsets = buildOnsetTicks(noteCount, endTick, accel);
  const contour = buildContour(
    noteCount,
    pool.length,
    startIdx,
    riseWeight,
    keepWeight,
    fallWeight,
    rand,
  );

  // 終止形スナップ（2 音以上のときのみ意味を持つ）
  if (contour.length >= 2) {
    const cadencePcs = getCadencePitchClasses(scale, cadence, rootPc);
    contour[contour.length - 1] = snapToCadence(
      pool,
      contour[contour.length - 1],
      contour[contour.length - 2],
      cadencePcs,
    );
  }

  // ベロシティ: 上昇優勢はクレッシェンド、下降優勢はデクレッシェンド
  const total = riseWeight + keepWeight + fallWeight;
  const drift = total > 0 ? (riseWeight - fallWeight) / total : 0;
  // ACCEL 連動のダイナミクス: + は最後の音へ向かって、− は頭側をわずかに
  // 強くする（±50 で最大 ±12% のなめらかな傾き）
  const accelTilt = (clamp(accel, -50, 50) / 50) * 0.12;
  const gateRatio = gate / 100;

  const notes: PhraseNote[] = contour.map((poolIdx, i) => {
    const tick = onsets[i];
    const nextGap =
      i < onsets.length - 1
        ? onsets[i + 1] - tick
        : onsets.length >= 2
          ? onsets[i] - onsets[i - 1]
          : PPQ;
    let durationTicks = Math.max(24, Math.round(nextGap * gateRatio));

    // グライド: 次の音へ 4 半音以上跳躍するときは音価を次の音までつなぎ、
    // 末尾でピッチを次の音へなめらかに遷移させる（MIDI では音の重なりに
    // なるため、DAW 側のレガート/グライドモードもそのまま反応する）
    let glideToMidi: number | undefined;
    if (glide && i < contour.length - 1) {
      const leap = Math.abs(pool[contour[i + 1]] - pool[poolIdx]);
      if (leap >= 4) {
        durationTicks = Math.max(durationTicks, onsets[i + 1] - tick);
        glideToMidi = pool[contour[i + 1]];
      }
    }

    const progress = noteCount > 1 ? i / (noteCount - 1) : 1;
    const dynamicCurve = 1 + drift * 0.28 * (progress - 0.5) * 2;
    const accelCurve = 1 + accelTilt * (progress * 2 - 1);
    const humanize = (rand() - 0.5) * 12;
    const accent = i === noteCount - 1 ? 10 : 0;
    const velocity = Math.round(
      clamp(92 * dynamicCurve * accelCurve + humanize + accent, 30, 127),
    );

    return {
      tick,
      midiNote: pool[poolIdx],
      durationTicks,
      velocity,
      ...(glideToMidi !== undefined ? { glideToMidi } : {}),
    };
  });

  const lengthTicks = Math.ceil((endTick + 1) / TICKS_PER_BAR) * TICKS_PER_BAR;

  return { notes, endTick, lengthTicks };
};

/** tick → 秒（BPM 固定の等速換算） */
export const ticksToSeconds = (ticks: number, bpm: number): number =>
  (ticks / PPQ) * (60 / bpm);

/** ミリ秒 → tick（アタックレイヤーのオフセット換算） */
export const msToTicks = (ms: number, bpm: number): number =>
  Math.round((ms / 1000) * (bpm / 60) * PPQ);
